import GlimpseCard from "../../components/card/card.component";
import GlimpseTable from "../../components/table/table.component";
import BarChart from "../../components/charts/bar-chart.component";
import { createColumnHelper } from "@tanstack/react-table";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  getAllPartners,
  getBarDailyUsuage,
  getCareerBadgesAdmin,
  getDashboardData,
  getDoughnutDailyUsuage,
  getMostPopularCareer,
} from "services/partners.service";
import {
  downloadReportFile,
  downloadReportFilePartners,
  getAllMembers,
} from "services/members.service";
import { IPartnerResult } from "interfaces/AddPartnersInterface";
import { ImemberInfo } from "interfaces/MembersInterface";
import { ITopThreeCareer } from "components/table/table.interface";
import GlimpseSmallCard from "components/card/smallCard.component";
import DughnutComponent from "components/charts/doughnut.component";
import Tabs from "components/tabs/tabs.component";
import { ITabItem } from "components/tabs/tabs.interface";
import { IDownloadReport, IPagination } from "interfaces/BaseInterface";
import { IFilterArray } from "components/table/table.interface";
import { filterItemsDatas } from "utills/Filter-Options";
import {
  PaginationFilter,
  StatusFilter,
  Top3Filter,
} from "enums/glimps.enums";
import {
  IBadgeReport,
  IBarData,
  IBarDataGet,
  IMostPopularGlimpse,
  MembershipData,
} from "interfaces/dashboard.interface";
import WeekRangePicker from "components/week-range-picker/weekPicker.component";
import { ChartData } from "chart.js";
import { useNavigate } from "react-router-dom";

const tabsList: ITabItem[] = [
  { name: "Overall" },
  { name: "Partners" },
  { name: "Individuals" },
];

const AdminDashboardScreen: React.FC = () => {
  const columnHelper = createColumnHelper<IPartnerResult>();
  const memberColumnHelper = createColumnHelper<ImemberInfo>();
  const navigation = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [partnerPagination, setPartnerPagination] = useState<IPagination>({
    page: 1,
    page_size: 25,
    top_ten: true,
    search: "",
    type: "",
  });
  const [partnerFullPagination, setPartnerFullPagination] = useState<IPagination>({
    page: 1,
    page_size: 25,
    top_ten: false,
    search: "",
    type: "",
  });
  const [membersPagination, setMembersPagination] = useState<IPagination>({
    page: 1,
    page_size: 25,
    top_ten: false,
    search: "",
    status: "",
    top_three: "",
  });
  const [topMembersPagination] = useState<IPagination>({
    page: 1,
    page_size: 10,
    top_ten: false,
    search: "",
    ordering: "-videos_watched",
  });
  const [membersFilterItems, setMembersFilterItems] = useState<IFilterArray[]>(
    () => JSON.parse(JSON.stringify(filterItemsDatas))
  );

  const [currentWeek, setCurrentWeek] = useState<Date>(new Date());

  useEffect(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diffStart = today.getDate() - dayOfWeek;
    // const diffEnd = diffStart + 6;
    const weekStart = new Date(today.setDate(diffStart));

    setCurrentWeek(weekStart);
  }, []);

  const dailyUsage: IBarDataGet = {
    start_date: currentWeek.toISOString().split("T")[0],
    user_type:
      activeTab === 0 ? "" : activeTab === 1 ? "partners" : "individuals",
  };

  const {
    data: partnerList,
    isLoading: isPartnerListLoading,
    refetch,
  } = useQuery({
    queryKey: ["GetAllPartners", partnerPagination],
    queryFn: () => getAllPartners(partnerPagination),
    enabled: !!partnerPagination && activeTab === 0,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  const {
    data: partnerFullList,
    isLoading: isPartnerFullListLoading,
  } = useQuery({
    queryKey: ["GetAllPartnersFull", partnerFullPagination],
    queryFn: () => getAllPartners(partnerFullPagination),
    enabled: activeTab === 1,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  const {
    data: membersList,
    isLoading: isMembersListLoading,
  } = useQuery({
    queryKey: ["GetAllSuperAdminMembers", membersPagination],
    queryFn: () => getAllMembers(membersPagination),
    enabled: activeTab === 2,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  const {
    data: topMembersList,
    isLoading: isTopMembersListLoading,
  } = useQuery({
    queryKey: ["GetTopSuperAdminMembers", topMembersPagination],
    queryFn: () => getAllMembers(topMembersPagination),
    enabled: activeTab === 0,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  const downloadMemberCsv = (data: Blob, baseName: string) => {
    const link = document.createElement("a");
    const blob = new Blob([data]);
    link.setAttribute("href", window.URL.createObjectURL(blob));
    link.setAttribute(
      "download",
      `${baseName} ${new Date().toDateString()}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadMembersReport = useMutation({
    mutationFn: (reportParams: IDownloadReport) => downloadReportFile(reportParams),
    onSuccess: (data: Blob) => downloadMemberCsv(data, "Individuals"),
  });

  const downloadPartnersReport = useMutation({
    mutationFn: (reportParams: IDownloadReport) =>
      downloadReportFilePartners(reportParams),
    onSuccess: (data: Blob) => downloadMemberCsv(data, "Partners"),
  });

  const partnerTableColumn = [
    columnHelper.accessor("name", {
      header: () => "Partner",
      cell: (info) => <span>{info.getValue()}</span>,
    }),
    columnHelper.accessor("total_licenses", {
      header: () => "Total Licenses",
      cell: (info) => <span>{info.getValue()}</span>,
    }),
    columnHelper.accessor("assigned_licence_count", {
      header: () => "Assigned Licenses",
      cell: (info) => <span>{info.getValue()}</span>,
    }),
    columnHelper.accessor("active_user_count", {
      header: () => "Activated Licenses",
      cell: (info) => <span>{info.getValue()}</span>,
    }),
    columnHelper.accessor("type", {
      header: () => "type",
      cell: (info) => <span className="capitalize">{info.getValue()}</span>,
    }),
  ];

  const topMembersTableColumn = [
    memberColumnHelper.accessor((row) => row.member?.name || row.email, {
      id: "individual",
      header: () => "Individual",
      cell: (info) => <span className="capitalize">{info.getValue() || "--"}</span>,
    }),
    memberColumnHelper.accessor("videos_watched", {
      header: () => "Previews",
      cell: (info) => <span>{info.getValue() ?? 0}</span>,
    }),
    memberColumnHelper.accessor("modules_completed", {
      header: () => "Deeper Dives",
      cell: (info) => <span>{info.getValue() ?? 0}</span>,
    }),
    memberColumnHelper.accessor("last_active", {
      header: () => "Last Active",
      cell: (info) => (
        <span>
          {info.getValue() ? new Date(info.getValue()).toDateString() : "--"}
        </span>
      ),
    }),
  ];

  const partnerFullTableColumn = [
    columnHelper.accessor("name", {
      header: () => "Partner",
      cell: (info) => <span>{info.getValue()}</span>,
    }),
    columnHelper.accessor("type", {
      header: () => "Type",
      cell: (info) => <span className="capitalize">{info.getValue()}</span>,
    }),
    columnHelper.accessor("total_licenses", {
      header: () => "Total Licenses",
      cell: (info) => <span>{info.getValue()}</span>,
    }),
    columnHelper.accessor("assigned_licence_count", {
      header: () => "Assigned",
      cell: (info) => <span>{info.getValue()}</span>,
    }),
    columnHelper.accessor("active_user_count", {
      header: () => "Activated",
      cell: (info) => <span>{info.getValue()}</span>,
    }),
    columnHelper.accessor(
      (row: any) => (row as IPartnerResult & { created_at?: string }).created_at,
      {
        id: "created_at",
        header: () => "Created",
        cell: (info) => (
          <span>
            {info.getValue()
              ? new Date(info.getValue() as string).toLocaleDateString()
              : "--"}
          </span>
        ),
      }
    ),
  ];

  const membersTableColumn = [
    memberColumnHelper.accessor("email", {
      header: () => "Email",
      cell: (info) => <span>{info.getValue()}</span>,
    }),
    memberColumnHelper.accessor((row) => row.member?.name, {
      id: "full_name",
      header: () => "Name",
      cell: (info) => <span className="capitalize">{info.getValue() || "--"}</span>,
    }),
    memberColumnHelper.accessor("date_joined", {
      header: () => "Sign Up Date",
      cell: (info) => (
        <span>
          {info.getValue()
            ? new Date(info.getValue() as string).toLocaleDateString()
            : "--"}
        </span>
      ),
    }),
    memberColumnHelper.accessor("status", {
      header: () => "Status",
      cell: (info) => <span className="capitalize">{info.getValue()}</span>,
    }),
    memberColumnHelper.accessor("top_three", {
      header: () => "Top Three",
      enableSorting: false,
      cell: (info) => (
        <div className="flex gap-1">
          {(info.getValue() as ITopThreeCareer[] | undefined)?.map(
            (item, index) => (
              <img
                key={index}
                className="min-w-[24px] h-[24px]"
                src={item.icon_url || ""}
                alt={item.name}
                title={item.name}
              />
            )
          )}
        </div>
      ),
    }),
    memberColumnHelper.accessor("videos_watched", {
      header: () => "Previews",
      cell: (info) => <span>{info.getValue()}</span>,
    }),
    memberColumnHelper.accessor("modules_completed", {
      header: () => "Deeper Dives",
      cell: (info) => <span>{info.getValue()}</span>,
    }),
    memberColumnHelper.accessor("last_active", {
      header: () => "Last Active",
      cell: (info) => (
        <span>
          {info.getValue() ? new Date(info.getValue()).toDateString() : "--"}
        </span>
      ),
    }),
  ];

  const tabChanged = (e: React.MouseEvent<HTMLDivElement>, index: number) => {
    setActiveTab(index);
  };

  const { data: dailyUsageData, status } = useQuery({
    queryKey: ["barDailyUsuage", dailyUsage],
    queryFn: () => getBarDailyUsuage(dailyUsage),
    enabled: !!dailyUsage,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  const label = dailyUsageData?.daily_usage.map((i: IBarData) => {
    return [i.date.day, i.date.date];
    // return [i.date.day.split("day")[0], i.date.date];
  });

  const barData: ChartData<"bar"> = {
    labels: label,
    datasets: [
      {
        label: "Time Spent",
        data:
          (dailyUsageData &&
            dailyUsageData.daily_usage.map((d) => d.time_spend)) ??
          [],
        backgroundColor: "#B0675A",
        borderRadius: {
          topLeft: 8,
          topRight: 8,
        },
      },
    ],
  };

  const { data: dailyUsageDataDoughnut } = useQuery({
    queryKey: ["doughnutDailyUsuage"],
    queryFn: () => getDoughnutDailyUsuage(),
    refetchOnWindowFocus: false,
    retry: 0,
  });

  const chartData: ChartData<"doughnut"> = {
    labels: dailyUsageDataDoughnut?.membership_data?.map(
      (d: MembershipData) => d.label
    ),
    datasets: [
      {
        data: dailyUsageDataDoughnut?.membership_data.map((d) => d.value) ?? [],
        backgroundColor: dailyUsageDataDoughnut?.membership_data?.map(
          (d) => d.color
        ),
      },
    ],
  };

  const { data: topGlimpse } = useQuery({
    queryKey: ["topGlimpse"],
    queryFn: () => getMostPopularCareer(),
    refetchOnWindowFocus: false,
    retry: 0,
  });

  const { data: iqLevel } = useQuery({
    queryKey: ["iqlevel"],
    queryFn: () => getCareerBadgesAdmin(),
    refetchOnWindowFocus: false,
    retry: 0,
  });

  const { data: dashboardData } = useQuery({
    queryKey: ["dashboardData"],
    queryFn: () => getDashboardData(),
    refetchOnWindowFocus: false,
    retry: 0,
  });

  return (
    <>
      {dashboardData && (
        <div className="flex justify-between gap-5 p-2 overflow-auto">
          <GlimpseSmallCard title={"Total Licenses"} className="min-w-[160px]">
            <span className="text-[24px] text-green font-semibold">
              {dashboardData.total_membership}
            </span>
          </GlimpseSmallCard>
          <GlimpseSmallCard
            title={"Total Weekly Hours"}
            className="min-w-[160px]"
          >
            <span className="text-[24px] text-green font-semibold">
              {dashboardData.total_weekly_hours}
            </span>
          </GlimpseSmallCard>
          <GlimpseSmallCard title={"Shared Top 3"} className="min-w-[160px]">
            <span className="text-[24px] text-green font-semibold">
              {dashboardData.shared_top_three}
            </span>
          </GlimpseSmallCard>
          <GlimpseSmallCard title={"Shared Top 10"} className="min-w-[160px]">
            <span className="text-[24px] text-green font-semibold">
              {dashboardData.shared_top_ten}
            </span>
          </GlimpseSmallCard>
        </div>
      )}

      <div className="flex flex-col gap-3 p-3 w-100">
        <p className="text-xl font-semibold leading-6">Activity</p>
        <div className="flex flex-row gap-5 p-2 overflow-auto">
          <GlimpseCard
            title="Daily Usage"
            className="min-w-[318px] max-w-[352px] h-[410px] relative overflow-hidden"
            childrenClass="h-full"
          >
            <div className="absolute flex justify-end top-2 right-2">
              <WeekRangePicker
                currentWeek={currentWeek}
                setCurrentWeek={setCurrentWeek}
              />
            </div>
            <div className="w-full overflow-auto ">
              <Tabs
                items={tabsList}
                onClick={tabChanged}
                activeIndex={activeTab}
                className="inline-table"
                tabFontSize="text-sm"
                opacity={false}
              />
            </div>
            <div className="h-[290px]">
              {barData && <BarChart data={barData} key={"AdminBarData"} />}
            </div>
          </GlimpseCard>
          <GlimpseCard
            title="Membership Type"
            className="min-w-[318px] max-w-[352px] !h-[395px]"
            childrenClass="h-full"
          >
            <div className="min-w-[227px] h-[227px] flex justify-center items-center">
              {dailyUsageDataDoughnut && chartData && (
                <DughnutComponent
                  data={chartData}
                  centerPercentage={true}
                  centerValue={
                    dailyUsageDataDoughnut?.total_users
                      ? dailyUsageDataDoughnut.total_users
                      : "Total Membership 0"
                  }
                  showDataLabels={true}
                  totalCount={
                    dailyUsageDataDoughnut?.total_users
                      ? parseInt(
                        dailyUsageDataDoughnut?.total_users.split(
                          "Total Memberships"
                        )[1]
                      )
                      : 0
                  }
                  fontStyle="400 12px sans-serif"
                />
              )}
            </div>
            <div className="flex justify-center">
              <div className="flex flex-col gap-2 pt-3">
                {dailyUsageDataDoughnut?.membership_data?.map((data, index) => {
                  return (
                    <span className="flex items-center gap-2" key={index}>
                      <span
                        className={`rounded-full w-[16px] h-[16px] bg-[${data.color}] `}
                        style={{
                          background: `${data.color}`,
                        }}
                      ></span>
                      <span className="text-xs">
                        {data.label} - {data.value}
                      </span>
                    </span>
                  );
                })}
              </div>
            </div>
          </GlimpseCard>
          <div className="min-w-[335px] min-h-[371px] flex flex-col gap-4">
            <GlimpseCard
              title="Career Awareness Levels"
              className="min-w-[335px] h-[197px]"
              childrenClass="flex justify-center items-center gap-3 pt-4"
            >
              {iqLevel?.map((item: IBadgeReport, index: number) => {
                return (
                  <div
                    key={index}
                    className="flex flex-col items-center justify-center text-sm text-center text-headerBlue"
                  >
                    <span className="px-2 py-2">
                      <img
                        src={`/assets/svg/badge-${item.slug}.svg`}
                        alt="badge"
                        className="w-[70px] h-auto"
                      />
                    </span>
                    <span className="tracking-wider capitalize">
                      {`${item.slug}`}
                    </span>
                    <span className="font-bold">{item.badge_user_count}</span>
                  </div>
                );
              })}
            </GlimpseCard>

            <GlimpseCard
              title="Most Popular Glimpses"
              className="min-w-[335px] h-[197px]"
              childrenClass="flex justify-between items-center pt-4"
            >
              {topGlimpse?.length === 0 ? (
                <span> Data Not Available </span>
              ) : (
                <>
                  {topGlimpse?.map((item: IMostPopularGlimpse, index) => {
                    return (
                      <div
                        key={index}
                        className="flex flex-col items-center justify-center text-center text-[10px] text-headerBlue "
                      >
                        <span className="px-2 py-2">
                          <img
                            src={item.icon_url}
                            alt={`top_glimpses_${item.name}`}
                            className="w-[70px] h-auto"
                          />
                        </span>
                        <span className={`tracking-wider`}>{item.name}</span>
                      </div>
                    );
                  })}
                </>
              )}
            </GlimpseCard>
          </div>
        </div>
      </div>
      {activeTab === 0 && (
        <div className="flex flex-col gap-3 p-3 overflow-auto overflow-y-hidden w-100">
          <p className="text-xl font-semibold leading-6">Top 10 Individuals</p>
          <GlimpseTable
            columns={topMembersTableColumn}
            data={topMembersList ? topMembersList.results : []}
            loadingData={isTopMembersListLoading}
            totalData={topMembersList?.count as number}
            currentPage={topMembersPagination.page}
            totalPage={1}
            enableFilter={false}
            enableExport={false}
            enableSearch={false}
            enablePagination={false}
            compactBottom
            preventClickOn={["id"]}
            rowSelected={(data) => {
              navigation(`/individuals/individual-insight/${data?.id}`);
            }}
            onSearch={() => {}}
            filterChanged={() => {}}
          />
          <p className="text-xl font-semibold leading-6">Top 10 Partners</p>
          <GlimpseTable
            columns={partnerTableColumn}
            data={partnerList ? partnerList.results : []}
            loadingData={isPartnerListLoading}
            totalData={partnerList?.count as number}
            currentPage={partnerPagination.page}
            totalPage={
              partnerList
                ? Math.ceil(partnerList.count / partnerPagination.page_size)
                : 0
            }
            enableFilter={false}
            enableExport={false}
            enableSearch={false}
            enablePagination={false}
            compactBottom
            preventClickOn={["id"]}
            rowSelected={(data) => {
              navigation(`/partners/partners-insight/${data?.id}`);
            }}
            onSearch={() => {}}
            filterChanged={() => {}}
          />
        </div>
      )}

      {activeTab === 1 && (
        <div className="flex flex-col gap-3 p-3 overflow-auto overflow-y-hidden w-100">
          <p className="text-xl font-semibold leading-6">Partners</p>
          <GlimpseTable
            columns={partnerFullTableColumn}
            data={partnerFullList ? partnerFullList.results : []}
            loadingData={isPartnerFullListLoading}
            totalData={partnerFullList?.count as number}
            currentPage={partnerFullPagination.page}
            totalPage={
              partnerFullList
                ? Math.ceil(
                    partnerFullList.count / partnerFullPagination.page_size
                  )
                : 0
            }
            enableFilter={false}
            enableExport={true}
            enableSearch={true}
            enablePagination={true}
            preventClickOn={["id"]}
            rowSelected={(data) => {
              navigation(`/partners/partners-insight/${data?.id}`);
            }}
            onSearch={(ev) => {
              setPartnerFullPagination((prev) => ({
                ...prev,
                page: 1,
                search: ev.target.value,
              }));
            }}
            filterChanged={() => {}}
            nextPage={() => {
              if (
                Math.ceil(
                  partnerFullList
                    ? partnerFullList.count / partnerFullPagination.page_size
                    : 0
                ) <= partnerFullPagination.page
              )
                return;
              setPartnerFullPagination((prev) => ({
                ...prev,
                page: prev.page + 1,
              }));
            }}
            previousPage={() => {
              if (partnerFullPagination.page <= 1) return;
              setPartnerFullPagination((prev) => ({
                ...prev,
                page: prev.page - 1,
              }));
            }}
            exportData={() => {
              downloadPartnersReport.mutateAsync({
                search: partnerFullPagination.search || null,
                type: (partnerFullPagination.type as string) || null,
              });
            }}
          />
        </div>
      )}

      {activeTab === 2 && (
        <div className="flex flex-col gap-3 p-3 overflow-auto overflow-y-hidden w-100">
          <p className="text-xl font-semibold leading-6">Individuals</p>
          <GlimpseTable
            columns={membersTableColumn}
            data={membersList ? membersList.results : []}
            loadingData={isMembersListLoading}
            totalData={membersList?.count as number}
            currentPage={membersPagination.page}
            totalPage={
              membersList
                ? Math.ceil(membersList.count / membersPagination.page_size)
                : 0
            }
            enableFilter={true}
            enableExport={true}
            enableSearch={true}
            enablePagination={true}
            preventClickOn={["id"]}
            filterItems={membersFilterItems}
            rowSelected={(data) => {
              if ((data as ImemberInfo).status === "active") {
                navigation(`/partner-dashboard/individual-insight/${data?.id}`);
              }
            }}
            onSearch={(ev) => {
              setMembersPagination((prev) => ({
                ...prev,
                page: 1,
                search: ev.target.value,
              }));
            }}
            filterChanged={(data) => {
              setMembersFilterItems(data);
              const values = data.flatMap((group) =>
                group.filters.filter((f) => f.is_checked)
              );
              setMembersPagination((prev) => ({
                ...prev,
                page: 1,
                status: "",
                top_three: "",
                email_subscribed: undefined,
              }));
              values.forEach((item) => {
                if (
                  item.name === StatusFilter.Active ||
                  item.name === StatusFilter.Invited
                ) {
                  setMembersPagination((prev) => ({
                    ...prev,
                    page: 1,
                    status: item.name as any,
                  }));
                }
                if (
                  item.name === Top3Filter.Assigned ||
                  item.name === Top3Filter.Unassigned
                ) {
                  setMembersPagination((prev) => ({
                    ...prev,
                    page: 1,
                    top_three: item.name as any,
                  }));
                }
                if (
                  item.name === PaginationFilter.ten ||
                  item.name === PaginationFilter.default ||
                  item.name === PaginationFilter.fifty
                ) {
                  setMembersPagination((prev) => ({
                    ...prev,
                    page: 1,
                    page_size: item.name as number,
                  }));
                }
              });
            }}
            nextPage={() => {
              if (
                Math.ceil(
                  membersList
                    ? membersList.count / membersPagination.page_size
                    : 0
                ) <= membersPagination.page
              )
                return;
              setMembersPagination((prev) => ({
                ...prev,
                page: prev.page + 1,
              }));
            }}
            previousPage={() => {
              if (membersPagination.page <= 1) return;
              setMembersPagination((prev) => ({
                ...prev,
                page: prev.page - 1,
              }));
            }}
            exportData={() => {
              downloadMembersReport.mutateAsync({
                search: membersPagination.search || null,
                status: (membersPagination.status as string) || null,
                top_three: (membersPagination.top_three as string) || null,
                email_subscribed:
                  membersPagination.email_subscribed === undefined
                    ? null
                    : membersPagination.email_subscribed,
                date_joined_from: membersPagination.date_joined_from || null,
                date_joined_to: membersPagination.date_joined_to || null,
              });
            }}
            dateRangeFilterEnabled
            dateRangeLabel="Sign Up Date"
            dateFrom={membersPagination.date_joined_from || ""}
            dateTo={membersPagination.date_joined_to || ""}
            onDateRangeChange={(from, to) => {
              setMembersPagination((prev) => ({
                ...prev,
                page: 1,
                date_joined_from: from || undefined,
                date_joined_to: to || undefined,
              }));
            }}
          />
        </div>
      )}
    </>
  );
};

export default AdminDashboardScreen;
